/**
 * Browser memory/runtime inventory for profiling & stress tests
 * (VistoeraTASK-00122). Callable from Playwright via `page.evaluate` —
 * zero production overhead when not invoked.
 */

export interface MemoryRuntimeSnapshot {
  ts: number
  scenario: string
  iteration: number
  jsHeapUsed: number | null
  jsHeapTotal: number | null
  jsHeapLimit: number | null
  domNodes: number
  canvasCount: number
  canvasTotalPixelBytesEstimate: number
  imageCount: number
  videoCount: number
  audioCount: number
  objectUrlActiveCount: number
  mediaSourceCount: number
  imageDataEstimateBytes: number
}

function estimateCanvasBackingBytes(): { count: number; bytes: number } {
  let count = 0
  let bytes = 0
  for (const el of document.querySelectorAll('canvas')) {
    const w = el.width
    const h = el.height
    if (w > 0 && h > 0) {
      count += 1
      bytes += w * h * 4
    }
  }
  return { count, bytes }
}

function countObjectUrls(): number {
  let n = 0
  for (const el of document.querySelectorAll('[src]')) {
    const src = el.getAttribute('src')
    if (src?.startsWith('blob:')) n += 1
  }
  return n
}

/** Best-effort DOM inventory — does not include GPU-only or decoded-image cache. */
export function collectMemoryRuntimeSnapshot(
  scenario: string,
  iteration = 0,
): MemoryRuntimeSnapshot {
  const perf = performance as Performance & {
    memory?: { usedJSHeapSize: number; totalJSHeapSize: number; jsHeapSizeLimit: number }
  }
  const mem = perf.memory
  const { count: canvasCount, bytes: canvasTotalPixelBytesEstimate } = estimateCanvasBackingBytes()

  return {
    ts: Date.now(),
    scenario,
    iteration,
    jsHeapUsed: mem?.usedJSHeapSize ?? null,
    jsHeapTotal: mem?.totalJSHeapSize ?? null,
    jsHeapLimit: mem?.jsHeapSizeLimit ?? null,
    domNodes: document.getElementsByTagName('*').length,
    canvasCount,
    canvasTotalPixelBytesEstimate,
    imageCount: document.querySelectorAll('img').length,
    videoCount: document.querySelectorAll('video').length,
    audioCount: document.querySelectorAll('audio').length,
    objectUrlActiveCount: countObjectUrls(),
    mediaSourceCount: typeof MediaSource !== 'undefined' ? 0 : 0,
    imageDataEstimateBytes: 0,
  }
}

declare global {
  interface Window {
    __vistoeraCollectMemorySnapshot?: (scenario: string, iteration?: number) => MemoryRuntimeSnapshot
    __vistoeraMemorySnapshots?: MemoryRuntimeSnapshot[]
  }
}

/** Dev/E2E hook — registers `window.__vistoeraCollectMemorySnapshot`. */
export function installMemoryRuntimeProbe(): void {
  if (typeof window === 'undefined') return
  window.__vistoeraCollectMemorySnapshot = (scenario, iteration = 0) => {
    const snap = collectMemoryRuntimeSnapshot(scenario, iteration)
    window.__vistoeraMemorySnapshots = window.__vistoeraMemorySnapshots || []
    window.__vistoeraMemorySnapshots.push(snap)
    return snap
  }
}
